import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, Wallet, ArrowLeftRight, Send, Inbox,
  History, AlertTriangle, Coins, Award, ShoppingBag, RefreshCw, X,
  ChevronRight, Plus, PieChart, Info, CreditCard, Trophy
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { useBinancePrices } from '../../hooks/useBinancePrices';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Wallet },
  { id: 'trade', label: 'Trade', icon: TrendingUp },
  { id: 'convert', label: 'Convert', icon: ArrowLeftRight },
  { id: 'p2p', label: 'P2P', icon: Send },
  { id: 'deposit', label: 'Deposit', icon: Inbox },
  { id: 'withdrawal', label: 'Withdraw', icon: CreditCard },
  { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
  { id: 'history', label: 'History', icon: History },
  { id: 'commission', label: 'Commission', icon: Award },
  { id: 'fee_breakdown', label: 'Fee Breakdown', icon: PieChart },
];

const AVX = () => {
  // Read initial tab from ?tab=... query param (e.g. from ATWalletBadge deep-link)
  const initialTab = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab')) || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [settings, setSettings] = useState(null);
  const [disclaimerStatus, setDisclaimerStatus] = useState({ accepted: false });
  const [loading, setLoading] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const [s, d] = await Promise.all([
        api.get('/avx/settings'),
        api.get('/avx/disclaimer/status'),
      ]);
      setSettings(s.data);
      setDisclaimerStatus(d.data);
      if (s.data?.fantasy_disclaimer_enabled && !d.data?.accepted) {
        setShowDisclaimer(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const acceptDisclaimer = async () => {
    try {
      await api.post('/avx/disclaimer/accept');
      setDisclaimerStatus({ accepted: true });
      setShowDisclaimer(false);
      toast.success('Disclaimer accepted. Welcome to AVX!');
    } catch (e) {
      toast.error('Could not accept disclaimer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
        <RefreshCw className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  if (!settings?.is_enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 px-6">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
          <h1 className="text-2xl font-bold mb-2">AVX is Currently Disabled</h1>
          <p className="text-slate-300">कृपया बाद में पुनः कोशिश करें।</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32 bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white">
      {/* Persistent Fantasy Disclaimer Bar */}
      <div className="bg-yellow-500/10 border-b border-yellow-500/30 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-2 text-xs text-yellow-200">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span data-testid="avx-disclaimer-bar">
            <strong>FANTASY SIMULATION GAME</strong> — All assets are virtual. Not a SEBI/RBI registered exchange.
            <button
              onClick={() => setShowDisclaimer(true)}
              className="ml-2 underline hover:text-yellow-100"
            >
              Read full disclaimer
            </button>
          </span>
        </div>
      </div>

      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
              ANANT Virtual Exchange
            </h1>
            <p className="text-slate-400 text-sm mt-1">AVX • Closed Beta • Fantasy Trading Simulator</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="text-sm text-emerald-300">Live</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-9 z-20">
        <div className="max-w-7xl mx-auto px-2 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`avx-tab-${t.id}`}
                  className={`px-4 py-3 flex items-center gap-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    active
                      ? 'border-yellow-400 text-yellow-300'
                      : 'border-transparent text-slate-400 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'overview' && <Overview settings={settings} />}
        {activeTab === 'trade' && <TradeTab settings={settings} />}
        {activeTab === 'convert' && <ConvertTab settings={settings} />}
        {activeTab === 'p2p' && <P2PTab settings={settings} />}
        {activeTab === 'deposit' && <DepositTab settings={settings} />}
        {activeTab === 'withdrawal' && <WithdrawalTab settings={settings} />}
        {activeTab === 'leaderboard' && <LeaderboardTab />}
        {activeTab === 'history' && <HistoryTab />}
        {activeTab === 'commission' && <CommissionTab />}
        {activeTab === 'fee_breakdown' && <FeeBreakdownTab settings={settings} />}
      </div>

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <DisclaimerModal
          settings={settings}
          accepted={disclaimerStatus.accepted}
          onAccept={acceptDisclaimer}
          onClose={() => disclaimerStatus.accepted && setShowDisclaimer(false)}
        />
      )}
    </div>
  );
};

// ============== DISCLAIMER MODAL ==============
const DisclaimerModal = ({ settings, accepted, onAccept, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border-2 border-yellow-500/50 rounded-2xl max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
        {accepted && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-8 h-8 text-yellow-400" />
          <h2 className="text-2xl font-bold text-yellow-300">Fantasy Disclaimer</h2>
        </div>
        <div className="text-slate-200 whitespace-pre-wrap leading-relaxed text-sm">
          {settings?.fantasy_disclaimer_text}
        </div>
        {!accepted && (
          <button
            onClick={onAccept}
            data-testid="accept-disclaimer-btn"
            className="mt-6 w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold rounded-lg hover:opacity-90 transition"
          >
            I Understand & Accept
          </button>
        )}
      </div>
    </div>
  );
};

// ============== OVERVIEW TAB ==============
const Overview = ({ settings }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    try {
      const r = await api.get('/avx/wallet/me');
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading...</div>;
  const w = data?.wallet || {};
  const totalValueAt = (w.at_balance || 0) + (data?.portfolio_value_at || 0) + (w.commission_balance || 0);

  return (
    <div className="space-y-6">
      {/* Total */}
      <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-2xl p-6">
        <div className="text-sm text-slate-300">Total Net Worth</div>
        <div className="text-4xl font-bold mt-1" data-testid="avx-total-worth">
          {totalValueAt.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          <span className="text-xl text-yellow-300 ml-2">AT</span>
        </div>
        <div className="text-xs text-slate-400 mt-2">
          Cash: ₹{(w.cash_balance || 0).toLocaleString()} • Portfolio: {(data?.portfolio_value_at || 0).toFixed(2)} AT
        </div>
      </div>

      {/* 3 Wallets */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <WalletCard label="Cash Wallet" value={`₹${(w.cash_balance || 0).toLocaleString()}`} hint="Real INR balance" gradient="from-emerald-500/20 to-teal-500/20" border="border-emerald-500/30" testid="wallet-cash" />
        <WalletCard label="Anant Token (AT)" value={`${(w.at_balance || 0).toFixed(2)} AT`} hint="Trade with this" gradient="from-yellow-500/20 to-orange-500/20" border="border-yellow-500/30" testid="wallet-at" />
        <WalletCard label="Commission Wallet" value={`${(w.commission_balance || 0).toFixed(2)} AT`} hint="Affiliate Network earnings" gradient="from-purple-500/20 to-pink-500/20" border="border-purple-500/30" testid="wallet-commission" />
      </div>

      {/* Holdings */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-yellow-400" />
          Your Holdings
        </h3>
        {(data?.holdings || []).length === 0 ? (
          <div className="text-center py-12 bg-slate-800/40 rounded-xl border border-slate-700">
            <p className="text-slate-400">No holdings yet. Start trading from the Trade tab.</p>
          </div>
        ) : (
          <div className="bg-slate-800/40 rounded-xl border border-slate-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-800 text-slate-300">
                <tr>
                  <th className="text-left p-3">Asset</th>
                  <th className="text-right p-3">Qty</th>
                  <th className="text-right p-3">Avg Buy</th>
                  <th className="text-right p-3">Current</th>
                  <th className="text-right p-3">Value</th>
                  <th className="text-right p-3">P/L</th>
                </tr>
              </thead>
              <tbody>
                {data.holdings.map((h) => {
                  const pl = (h.current_value || 0) - (h.total_invested || 0);
                  const plPct = h.total_invested ? (pl / h.total_invested) * 100 : 0;
                  return (
                    <tr key={h.id} className="border-t border-slate-700">
                      <td className="p-3 font-semibold">{h.asset_symbol}</td>
                      <td className="p-3 text-right">{h.quantity}</td>
                      <td className="p-3 text-right">{(h.avg_buy_rate || 0).toFixed(2)}</td>
                      <td className="p-3 text-right">{(h.current_rate || 0).toFixed(2)}</td>
                      <td className="p-3 text-right">{(h.current_value || 0).toFixed(2)}</td>
                      <td className={`p-3 text-right ${pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {pl >= 0 ? '+' : ''}{pl.toFixed(2)} ({plPct.toFixed(1)}%)
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const WalletCard = ({ label, value, hint, gradient, border, testid }) => (
  <div className={`bg-gradient-to-br ${gradient} border ${border} rounded-xl p-4`} data-testid={testid}>
    <div className="text-sm text-slate-300">{label}</div>
    <div className="text-2xl font-bold mt-1">{value}</div>
    <div className="text-xs text-slate-400 mt-1">{hint}</div>
  </div>
);

const SyncIndicator = ({ selected, settings, live }) => {
  const lastSyncedAt = selected.last_synced_at;
  const maxAge = settings?.max_sync_age_minutes || 60;
  if (!lastSyncedAt) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4 text-xs text-yellow-200 flex items-center gap-2">
        <span>⚠️</span>
        <span>Asset rate has not been synced from Binance yet. Trading is blocked. Admin will sync soon.</span>
      </div>
    );
  }
  const ageMin = (Date.now() - new Date(lastSyncedAt).getTime()) / 60000;
  const stale = ageMin > maxAge;
  const ageLabel = ageMin < 1 ? 'just now' : ageMin < 60 ? `${Math.floor(ageMin)} min ago` : `${Math.floor(ageMin / 60)}h ago`;
  return (
    <div className={`rounded-lg p-3 mb-4 text-xs flex items-center gap-2 ${
      stale ? 'bg-red-500/10 border border-red-500/30 text-red-200' : 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-200'
    }`}>
      <span>{stale ? '🚫' : '✓'}</span>
      <span>
        Last synced: <strong>{ageLabel}</strong>
        {selected.binance_last_price_usd ? ` at $${selected.binance_last_price_usd}` : ''}
        {stale ? ` — Trading blocked (>${maxAge}min old). Admin needs to sync.` : ''}
      </span>
    </div>
  );
};

const TradeTab = ({ settings }) => {
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [side, setSide] = useState('buy');
  const [qty, setQty] = useState('');
  const [wallet, setWallet] = useState({ at_balance: 0 });
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Live Binance prices for assets that have a binance_symbol
  const binanceSymbols = assets.filter(a => a.binance_symbol).map(a => a.binance_symbol);
  const { prices: livePrices } = useBinancePrices(binanceSymbols, 10000);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [a, w] = await Promise.all([api.get('/avx/assets'), api.get('/avx/wallet/me')]);
      setAssets(a.data?.assets || []);
      setWallet(w.data?.wallet || {});
      setHoldings(w.data?.holdings || []);
      if (!selected && (a.data?.assets || []).length > 0) {
        setSelected(a.data.assets[0]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    const q = parseFloat(qty);
    if (!selected || !q || q <= 0) {
      toast.error('Enter valid quantity');
      return;
    }
    try {
      const r = await api.post(`/avx/trade/${side}`, { asset_id: selected.id, quantity: q });
      toast.success(`${side === 'buy' ? 'Bought' : 'Sold'} ${q} ${selected.symbol}`);
      setQty('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Trade failed');
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading assets...</div>;
  if (assets.length === 0) {
    return <div className="text-center py-12 text-slate-400">No assets available yet. Admin needs to add assets.</div>;
  }

  const heldQty = (holdings.find(h => h.asset_id === selected?.id) || {}).quantity || 0;
  const total = (selected?.current_rate || 0) * (parseFloat(qty) || 0);
  const fee = total * (settings?.trading_fee_percent || 3) / 100;
  const minTrade = settings?.min_trade || 1000;
  const meetsMin = total >= minTrade;
  const live = selected?.binance_symbol ? livePrices[selected.binance_symbol] : null;
  const tvSymbol = selected?.tradingview_symbol
    || (selected?.binance_symbol ? `BINANCE:${selected.binance_symbol}` : selected?.symbol);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Asset list */}
      <div className="lg:col-span-1 space-y-2 max-h-[700px] overflow-y-auto">
        <h3 className="text-sm font-semibold text-slate-300 mb-2">
          Markets <span className="text-xs text-slate-500 ml-1">(Live every 10s)</span>
        </h3>
        {assets.map((a) => {
          const liveData = a.binance_symbol ? livePrices[a.binance_symbol] : null;
          const change = liveData?.change_24h_pct;
          return (
            <button
              key={a.id}
              onClick={() => setSelected(a)}
              data-testid={`asset-${a.symbol}`}
              className={`w-full text-left p-3 rounded-lg border transition ${
                selected?.id === a.id
                  ? 'bg-yellow-500/10 border-yellow-500/50'
                  : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold flex items-center gap-1">
                    {a.symbol}
                    {a.binance_symbol && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" title="Live" />}
                  </div>
                  <div className="text-xs text-slate-400">{a.name}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-yellow-300 text-sm">{a.current_rate} <span className="text-xs text-slate-500">AT</span></div>
                  {liveData && (
                    <div className={`text-xs ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Trade panel */}
      <div className="lg:col-span-2 space-y-4">
        {selected && (
          <>
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    {selected.name}
                    {selected.binance_symbol && (
                      <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded-full flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
                      </span>
                    )}
                  </h2>
                  <div className="text-sm text-slate-400">
                    {selected.symbol} • {selected.category}
                    {selected.binance_symbol && <span className="ml-2 text-slate-500">• {selected.binance_symbol}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-yellow-300">{selected.current_rate} <span className="text-lg">AT</span></div>
                  {live && (
                    <div className="text-xs text-slate-400 mt-1">
                      Binance Live: <span className="text-white font-semibold">${live.price.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Live 24h Stats Strip */}
              {live && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div>
                    <div className="text-xs text-slate-500">24h Change</div>
                    <div className={`text-sm font-bold ${live.change_24h_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {live.change_24h_pct >= 0 ? '+' : ''}{live.change_24h_pct.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">24h High</div>
                    <div className="text-sm font-bold text-white">${live.high_24h.toLocaleString(undefined, {maximumFractionDigits: 4})}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">24h Low</div>
                    <div className="text-sm font-bold text-white">${live.low_24h.toLocaleString(undefined, {maximumFractionDigits: 4})}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">24h Volume</div>
                    <div className="text-sm font-bold text-white">{(live.volume_24h / 1000).toFixed(1)}K</div>
                  </div>
                </div>
              )}

              {/* Sync staleness indicator */}
              {selected.binance_symbol && (
                <SyncIndicator selected={selected} settings={settings} live={live} />
              )}

              {/* TradingView Advanced Chart */}
              <div className="rounded-lg overflow-hidden bg-slate-950 border border-slate-700 mb-4" style={{ height: 380 }}>
                <iframe
                  key={tvSymbol}
                  title={`tv-${selected.symbol}`}
                  src={`https://s.tradingview.com/widgetembed/?frameElementId=tv-chart&symbol=${encodeURIComponent(tvSymbol)}&interval=15&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=%5B%5D&theme=dark&style=1&timezone=Asia%2FKolkata&withdateranges=1&hideideas=1`}
                  className="w-full h-full"
                  frameBorder="0"
                />
              </div>

              <div className="text-xs text-slate-400 mb-4">
                Holdings: <strong>{heldQty}</strong> {selected.symbol} • Available AT: <strong>{(wallet.at_balance || 0).toFixed(2)}</strong>
              </div>

              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setSide('buy')}
                  data-testid="trade-side-buy"
                  className={`flex-1 py-2 rounded-lg font-semibold ${side === 'buy' ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                >
                  Buy
                </button>
                <button
                  onClick={() => setSide('sell')}
                  data-testid="trade-side-sell"
                  className={`flex-1 py-2 rounded-lg font-semibold ${side === 'sell' ? 'bg-red-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                >
                  Sell
                </button>
              </div>

              <input
                type="number"
                placeholder={`Quantity to ${side} (Min ${minTrade} AT worth)`}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                data-testid="trade-quantity-input"
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3"
              />

              <div className="bg-slate-900/50 rounded-lg p-3 text-sm space-y-1 mb-4">
                <div className="flex justify-between text-slate-400"><span>Subtotal:</span><span>{total.toFixed(2)} AT</span></div>
                <div className="flex justify-between text-slate-400"><span>Fee ({settings?.trading_fee_percent}%):</span><span>{fee.toFixed(2)} AT</span></div>
                <div className="flex justify-between text-white font-semibold pt-2 border-t border-slate-700">
                  <span>Total {side === 'buy' ? 'Cost' : 'Receive'}:</span>
                  <span>{(side === 'buy' ? total + fee : total - fee).toFixed(2)} AT</span>
                </div>
                {qty && !meetsMin && (
                  <div className="text-xs text-red-400 pt-2">⚠️ Minimum trade value is {minTrade} AT (currently {total.toFixed(2)} AT)</div>
                )}
              </div>

              <button
                onClick={submit}
                disabled={!meetsMin}
                data-testid="trade-submit-btn"
                className={`w-full py-3 rounded-lg font-bold text-white transition ${
                  !meetsMin ? 'bg-slate-700 cursor-not-allowed opacity-60' :
                  side === 'buy' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {side === 'buy' ? 'Place Buy Order' : 'Place Sell Order'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============== CONVERT TAB ==============
const ConvertTab = ({ settings }) => {
  const [direction, setDirection] = useState('inr_to_at');
  const [amount, setAmount] = useState('');
  const [wallet, setWallet] = useState({});
  const rate = settings?.inr_to_at_rate || 1;

  useEffect(() => { loadWallet(); }, []);
  const loadWallet = async () => {
    try { const r = await api.get('/avx/wallet/me'); setWallet(r.data?.wallet || {}); }
    catch (e) { /* ignore */ }
  };

  const submit = async () => {
    const a = parseFloat(amount);
    if (!a || a <= 0) return toast.error('Enter valid amount');
    try {
      const url = direction === 'inr_to_at' ? '/avx/convert/inr-to-at' : '/avx/convert/at-to-inr';
      const r = await api.post(url, { amount: a });
      toast.success(`Conversion successful!`);
      setAmount('');
      loadWallet();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
    }
  };

  const fromBalance = direction === 'inr_to_at' ? wallet.cash_balance : wallet.at_balance;
  const fromLabel = direction === 'inr_to_at' ? 'Cash (₹)' : 'AT';
  const toLabel = direction === 'inr_to_at' ? 'AT' : 'Cash (₹)';
  const converted = direction === 'inr_to_at' ? (parseFloat(amount) || 0) * rate : (parseFloat(amount) || 0) / rate;

  return (
    <div className="max-w-md mx-auto bg-slate-800/40 border border-slate-700 rounded-2xl p-6">
      <h2 className="text-xl font-bold mb-2">Convert</h2>
      <p className="text-sm text-slate-400 mb-6">Rate: 1 INR = {rate} AT</p>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setDirection('inr_to_at')}
          data-testid="convert-inr-to-at"
          className={`flex-1 py-2 rounded-lg font-semibold ${direction === 'inr_to_at' ? 'bg-yellow-500 text-black' : 'bg-slate-700'}`}
        >
          ₹ → AT
        </button>
        <button
          onClick={() => setDirection('at_to_inr')}
          data-testid="convert-at-to-inr"
          className={`flex-1 py-2 rounded-lg font-semibold ${direction === 'at_to_inr' ? 'bg-yellow-500 text-black' : 'bg-slate-700'}`}
        >
          AT → ₹
        </button>
      </div>

      <label className="text-xs text-slate-400">From {fromLabel} (Available: {(fromBalance || 0).toFixed(2)})</label>
      <input
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        data-testid="convert-amount-input"
        placeholder="Amount"
        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg my-2"
      />
      <div className="text-center text-slate-400 my-2">↓</div>
      <div className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-3">
        <div className="text-xs text-slate-400">You receive ({toLabel})</div>
        <div className="text-2xl font-bold text-yellow-300">{converted.toFixed(2)}</div>
      </div>

      <button
        onClick={submit}
        data-testid="convert-submit-btn"
        className="w-full mt-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold rounded-lg"
      >
        Convert Now
      </button>
    </div>
  );
};

// ============== P2P TAB (Send / Receive / Scan & Pay) ==============
const P2PTab = ({ settings }) => {
  const [mode, setMode] = useState('send'); // 'send' | 'receive' | 'scan'
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [walletType, setWalletType] = useState('at'); // 'at' | 'commission'
  const [history, setHistory] = useState({ sent: [], received: [] });
  const [wallet, setWallet] = useState({});
  const [me, setMe] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [h, w, m] = await Promise.all([
        api.get('/avx/p2p/me'),
        api.get('/avx/wallet/me'),
        api.get('/auth/me').catch(() => ({ data: null })),
      ]);
      setHistory(h.data || {});
      setWallet(w.data?.wallet || {});
      setMe(m.data || null);
    } catch (e) { /* ignore */ }
  };

  const submit = async () => {
    setSendError('');
    const a = parseFloat(amount);
    const cleanRecipient = (recipient || '').trim().replace(/^@/, '');
    if (!cleanRecipient) { setSendError('Recipient username daalein'); toast.error('Recipient username daalein'); return; }
    if (!a || a <= 0) { setSendError('Sahi amount daalein'); toast.error('Sahi amount daalein'); return; }
    if (a + fee > availableBal) { setSendError(`Insufficient balance. Available: ${availableBal.toFixed(2)} AT`); toast.error('Insufficient balance'); return; }
    setSending(true);
    try {
      const endpoint = walletType === 'commission' ? '/avx/p2p/transfer-commission' : '/avx/p2p/transfer';
      const r = await api.post(endpoint, { recipient_username: cleanRecipient, amount: a, note });
      toast.success(`✅ ${a} AT transferred to @${cleanRecipient}${walletType === 'commission' ? ' (Commission wallet)' : ''} · Fee ${r.data.fee} AT`);
      setRecipient(''); setAmount(''); setNote('');
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Transfer failed';
      setSendError(msg);
      toast.error(msg);
    } finally { setSending(false); }
  };

  const feePct = walletType === 'commission' ? 3 : (settings?.p2p_fee_percent || 2);
  const fee = (parseFloat(amount) || 0) * feePct / 100;
  const availableBal = walletType === 'commission' ? (wallet.commission_balance || 0) : (wallet.at_balance || 0);
  const myUsername = me?.username || '';
  const myQrPayload = myUsername ? `AT:${myUsername}` : '';
  const myQrImg = myUsername ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(myQrPayload)}` : '';

  return (
    <div className="space-y-4" data-testid="p2p-root">
      {/* AT Balance strip */}
      <div className="bg-gradient-to-r from-amber-500/15 to-orange-500/10 border border-amber-400/30 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-amber-300 font-bold">AT Wallet Balance</div>
          <div className="text-3xl font-extrabold text-amber-100" data-testid="p2p-at-balance">{(wallet.at_balance || 0).toFixed(2)} <span className="text-sm font-normal text-amber-300/80">AT</span></div>
          <div className="text-[11px] text-emerald-300 mt-0.5">Commission: {(wallet.commission_balance || 0).toFixed(2)} AT</div>
        </div>
        {/* Send / Receive / Scan & Pay mode toggle */}
        <div className="flex gap-1.5">
          {[
            { id: 'send', label: 'Send', icon: Send },
            { id: 'receive', label: 'Receive', icon: Inbox },
            { id: 'scan', label: 'Scan & Pay', icon: TrendingUp },
          ].map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)} data-testid={`p2p-mode-${m.id}`}
              className={`px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1 transition ${mode === m.id ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}>
              <m.icon className="w-3.5 h-3.5" /> {m.label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'receive' && (
        <div className="bg-slate-800/40 border border-emerald-400/30 rounded-2xl p-6 text-center" data-testid="p2p-receive-panel">
          <h2 className="text-xl font-bold mb-1">Receive AT</h2>
          <p className="text-xs text-slate-400 mb-4">Sender ko apna username batayein ya QR scan karne dein</p>
          {myUsername ? (
            <>
              <div className="bg-white rounded-2xl p-4 inline-block mb-3">
                <img src={myQrImg} alt="My P2P QR" style={{ width: 240, height: 240 }} data-testid="p2p-my-qr" />
              </div>
              <div className="text-lg font-mono font-bold text-emerald-300" data-testid="p2p-my-username">@{myUsername}</div>
              <div className="text-[11px] text-slate-500 mt-1">QR payload: <code>{myQrPayload}</code></div>
              <div className="mt-4 flex gap-2 justify-center flex-wrap">
                <button onClick={() => { navigator.clipboard.writeText(myUsername); toast.success('Username copied'); }} className="px-3 py-1.5 rounded-lg bg-white/10 text-xs font-bold">Copy Username</button>
                <a href={myQrImg} download={`anant-p2p-${myUsername}.png`} className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold">Download QR</a>
              </div>
            </>
          ) : <div className="text-sm text-slate-400">Loading…</div>}
        </div>
      )}

      {mode === 'scan' && (
        <P2PScanner
          onScanned={(username) => { setRecipient(username); setMode('send'); toast.success(`Recipient set: @${username}`); }}
        />
      )}

      {mode === 'send' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-2">Send AT to Anyone</h2>

            {/* Wallet selector */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={() => setWalletType('at')} data-testid="p2p-wallet-at"
                className={`rounded-xl border p-3 text-left transition ${walletType === 'at' ? 'border-purple-400 bg-purple-500/15' : 'border-slate-700 bg-slate-900/40'}`}>
                <div className="text-xs font-bold">💰 AT Wallet</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{(wallet.at_balance || 0).toFixed(2)} AT · Fee {settings?.p2p_fee_percent || 2}%</div>
              </button>
              <button onClick={() => setWalletType('commission')} data-testid="p2p-wallet-commission"
                className={`rounded-xl border p-3 text-left transition ${walletType === 'commission' ? 'border-emerald-400 bg-emerald-500/15' : 'border-slate-700 bg-slate-900/40'}`}>
                <div className="text-xs font-bold">🏆 Commission Wallet</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{(wallet.commission_balance || 0).toFixed(2)} AT · Fee 3%</div>
              </button>
            </div>
            <p className="text-xs text-slate-400 mb-4">Available: {availableBal.toFixed(2)} AT • Fee: {feePct}%{walletType === 'commission' ? ' • Recipient ke AT wallet mein jayega' : ''}</p>

            <div className="flex gap-2 mb-3">
              <input
                type="text"
                placeholder="Recipient username"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                data-testid="p2p-recipient"
                className="flex-1 px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg"
              />
              <button onClick={() => setMode('scan')} data-testid="p2p-scan-shortcut" className="px-3 rounded-lg bg-white/10 text-xs font-bold flex items-center gap-1">📷 Scan</button>
            </div>
            <input
              type="number"
              placeholder="Amount (AT)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="p2p-amount"
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3"
            />
            <input
              type="text"
              placeholder="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3"
            />

            <div className="bg-slate-900/50 rounded-lg p-3 text-sm mb-4">
              <div className="flex justify-between text-slate-400"><span>Send:</span><span>{(parseFloat(amount) || 0).toFixed(2)} AT</span></div>
              <div className="flex justify-between text-slate-400"><span>Fee:</span><span>{fee.toFixed(2)} AT</span></div>
              <div className="flex justify-between text-white font-semibold border-t border-slate-700 pt-2 mt-2">
                <span>Total Debit:</span><span>{((parseFloat(amount) || 0) + fee).toFixed(2)} AT</span>
              </div>
            </div>

            {sendError && (
              <div className="mb-3 p-2.5 rounded-lg bg-red-500/15 border border-red-400/40 text-red-200 text-xs" data-testid="p2p-send-error">
                ⚠️ {sendError}
              </div>
            )}

            <button
              onClick={submit}
              disabled={sending}
              data-testid="p2p-send-btn"
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-bold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending…' : 'Send Now'}
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-2">Recent Sent</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {(history.sent || []).slice(0, 10).map((t) => (
                  <div key={t.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-sm">
                    <div className="flex justify-between"><span>To: <strong>{t.recipient_username}</strong>{t.wallet === 'commission' && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold">COMM</span>}</span><span className="text-red-400">-{t.amount} AT</span></div>
                    <div className="text-xs text-slate-500 mt-1">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                ))}
                {(history.sent || []).length === 0 && <div className="text-slate-500 text-sm">No transfers yet</div>}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-2">Recent Received</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {(history.received || []).slice(0, 10).map((t) => (
                  <div key={t.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-sm">
                    <div className="flex justify-between"><span>From: <strong>{t.sender_username}</strong></span><span className="text-emerald-400">+{t.amount} AT</span></div>
                    <div className="text-xs text-slate-500 mt-1">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                ))}
                {(history.received || []).length === 0 && <div className="text-slate-500 text-sm">No transfers yet</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// P2P QR scanner — reads "AT:username" or plain username string and returns via onScanned
const P2PScanner = ({ onScanned }) => {
  const [Scanner, setScanner] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    import('@yudiel/react-qr-scanner').then((m) => setScanner(() => m.Scanner)).catch((e) => setErr(String(e)));
  }, []);
  const handle = (results) => {
    const raw = Array.isArray(results) ? (results[0]?.rawValue || '') : (results?.rawValue || String(results || ''));
    if (!raw) return;
    let username = raw.trim();
    if (username.toUpperCase().startsWith('AT:')) username = username.slice(3).trim();
    // strip any URL wrappers → last path segment
    if (username.includes('/')) username = username.split('/').filter(Boolean).pop();
    username = username.replace(/[^A-Za-z0-9_.-]/g, '');
    if (username) onScanned(username);
  };
  return (
    <div className="bg-slate-800/40 border border-purple-400/30 rounded-2xl p-4" data-testid="p2p-scan-panel">
      <h2 className="text-lg font-bold mb-2">Scan & Pay</h2>
      <p className="text-xs text-slate-400 mb-3">Recipient ka Anant P2P QR camera par point karein. Camera permission ki zaroorat hogi.</p>
      {err && <div className="text-xs text-red-300 mb-2">Scanner load nahi hua: {err}</div>}
      <div className="rounded-xl overflow-hidden bg-black" style={{ maxWidth: 420, margin: '0 auto' }}>
        {Scanner ? (
          <Scanner
            onScan={handle}
            onError={(e) => setErr(e?.message || 'Camera error')}
            constraints={{ facingMode: 'environment' }}
            styles={{ container: { width: '100%' } }}
          />
        ) : <div className="text-center py-8 text-slate-400 text-sm">Loading camera…</div>}
      </div>
      <div className="text-[11px] text-slate-500 mt-2 text-center">Tip: QR "AT:username" format mein encode hota hai.</div>
    </div>
  );
};

// ============== DEPOSIT TAB ==============
const DepositTab = ({ settings }) => {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('upi');
  const [proof, setProof] = useState('');
  const [note, setNote] = useState('');
  const [requests, setRequests] = useState([]);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await api.get('/avx/deposit/requests/me'); setRequests(r.data?.requests || []); }
    catch (e) { /* ignore */ }
  };

  const submit = async () => {
    const a = parseFloat(amount);
    if (!a || a < (settings?.min_deposit || 100)) return toast.error(`Min deposit ₹${settings?.min_deposit}`);
    try {
      await api.post('/avx/deposit/request', { amount: a, payment_method: method, payment_proof: proof, note });
      toast.success('Deposit request submitted! Wait for admin approval.');
      setAmount(''); setProof(''); setNote('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-6" data-testid="avx-deposit-form">
        <h2 className="text-xl font-bold mb-2">Request Deposit</h2>
        <p className="text-xs text-slate-400 mb-4">Min: ₹{settings?.min_deposit} • Manual approval by admin</p>

        {/* Payment Instructions (admin-managed) */}
        {(settings?.deposit_upi_id || settings?.deposit_upi_qr_url || settings?.deposit_bank_account) && (
          <div className="bg-slate-900/60 border border-emerald-700/40 rounded-xl p-4 mb-4" data-testid="avx-deposit-payment-info">
            <div className="text-xs font-semibold text-emerald-300 mb-3">💳 Payment Details — पहले payment करें, फिर नीचे form भरें</div>

            {settings?.deposit_upi_qr_url && (
              <div className="mb-3 flex items-center gap-3">
                <img src={settings.deposit_upi_qr_url} alt="UPI QR" className="w-28 h-28 bg-white rounded-lg p-1" data-testid="avx-deposit-upi-qr" />
                <div className="text-xs">
                  <div className="text-slate-400">Scan to pay via UPI</div>
                  {settings?.deposit_upi_id && (
                    <div className="text-slate-200 font-mono mt-1 break-all">{settings.deposit_upi_id}</div>
                  )}
                </div>
              </div>
            )}
            {!settings?.deposit_upi_qr_url && settings?.deposit_upi_id && (
              <div className="text-xs mb-2"><span className="text-slate-400">UPI ID:</span> <span className="text-slate-200 font-mono">{settings.deposit_upi_id}</span></div>
            )}

            {settings?.deposit_bank_account && (
              <div className="text-xs space-y-0.5 border-t border-slate-700 pt-3 mt-2">
                <div className="text-slate-400 font-semibold mb-1">Bank Transfer:</div>
                {settings?.deposit_bank_holder && <div><span className="text-slate-500">Holder:</span> <span className="text-slate-200">{settings.deposit_bank_holder}</span></div>}
                {settings?.deposit_bank_name && <div><span className="text-slate-500">Bank:</span> <span className="text-slate-200">{settings.deposit_bank_name}</span></div>}
                <div><span className="text-slate-500">A/C:</span> <span className="text-slate-200 font-mono">{settings.deposit_bank_account}</span></div>
                {settings?.deposit_bank_ifsc && <div><span className="text-slate-500">IFSC:</span> <span className="text-slate-200 font-mono">{settings.deposit_bank_ifsc}</span></div>}
              </div>
            )}

            {settings?.deposit_instructions && (
              <div className="text-xs text-amber-300 mt-3 italic whitespace-pre-line">{settings.deposit_instructions}</div>
            )}

            {settings?.deposit_support_whatsapp && (
              <a
                href={`https://wa.me/${String(settings.deposit_support_whatsapp).replace(/\D/g, '')}?text=${encodeURIComponent('नमस्ते, मैंने AVX wallet के लिए deposit किया है। Payment screenshot भेज रहा हूँ।')}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="avx-deposit-whatsapp-btn"
                className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-semibold transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                WhatsApp पर Screenshot भेजें
              </a>
            )}
          </div>
        )}

        <input
          type="number"
          placeholder="Amount (₹)"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="deposit-amount"
          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3"
        />

        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3"
        >
          <option value="upi">UPI</option>
          <option value="bank">Bank Transfer</option>
          <option value="manual">Manual / Other</option>
        </select>

        <input
          type="text"
          placeholder="UTR / Transaction ID (optional)"
          value={proof}
          onChange={(e) => setProof(e.target.value)}
          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3"
        />

        <textarea
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3"
        />

        <button
          onClick={submit}
          data-testid="deposit-submit-btn"
          className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg font-bold"
        >
          Submit Request
        </button>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-300 mb-2">My Deposits</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {requests.map((r) => (
            <div key={r.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span className="font-semibold">₹{r.amount}</span>
                <span className={`text-xs px-2 py-1 rounded ${r.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : r.status === 'rejected' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                  {r.status}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{new Date(r.created_at).toLocaleString()}</div>
              {r.admin_note && <div className="text-xs text-slate-400 mt-1 italic">"{r.admin_note}"</div>}
            </div>
          ))}
          {requests.length === 0 && <div className="text-slate-500 text-sm">No deposit requests yet</div>}
        </div>
      </div>
    </div>
  );
};

// ============== HISTORY TAB ==============
const HistoryTab = () => {
  const [txs, setTxs] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    Promise.all([api.get('/avx/transactions/me'), api.get('/avx/orders/me')]).then(([t, o]) => {
      setTxs(t.data?.transactions || []);
      setOrders(o.data?.orders || []);
    });
  }, []);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <h3 className="text-sm font-semibold text-slate-300 mb-2">Transactions</h3>
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl divide-y divide-slate-700 max-h-[500px] overflow-y-auto">
          {txs.map((t) => (
            <div key={t.id} className="p-3 text-sm flex items-center justify-between">
              <div>
                <div className="font-semibold capitalize">{t.type.replace(/_/g, ' ')}</div>
                <div className="text-xs text-slate-500">{t.description}</div>
                <div className="text-xs text-slate-600 mt-1">{new Date(t.created_at).toLocaleString()}</div>
              </div>
              <div className={`text-right ${t.direction === 'credit' ? 'text-emerald-400' : 'text-red-400'}`}>
                <div className="font-bold">{t.direction === 'credit' ? '+' : '-'}{t.amount.toFixed(2)}</div>
                <div className="text-xs text-slate-500">{t.wallet}</div>
              </div>
            </div>
          ))}
          {txs.length === 0 && <div className="p-6 text-center text-slate-500">No transactions</div>}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-300 mb-2">Orders</h3>
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl divide-y divide-slate-700 max-h-[500px] overflow-y-auto">
          {orders.map((o) => (
            <div key={o.id} className="p-3 text-sm flex items-center justify-between">
              <div>
                <div className="font-semibold">
                  <span className={o.type === 'buy' ? 'text-emerald-400' : 'text-red-400'}>{o.type.toUpperCase()}</span> {o.quantity} {o.asset_symbol}
                </div>
                <div className="text-xs text-slate-500">@ {o.rate} AT • Fee {o.fee.toFixed(2)}</div>
                <div className="text-xs text-slate-600 mt-1">{new Date(o.created_at).toLocaleString()}</div>
              </div>
              <div className="text-yellow-300 font-bold">{o.gross.toFixed(2)} AT</div>
            </div>
          ))}
          {orders.length === 0 && <div className="p-6 text-center text-slate-500">No orders</div>}
        </div>
      </div>
    </div>
  );
};

// ============== COMMISSION TAB ==============
const CommissionTab = () => {
  const [data, setData] = useState({ recent: [], by_level: {} });

  useEffect(() => {
    api.get('/avx/commission/me').then((r) => setData(r.data || { recent: [], by_level: {} }));
  }, []);

  const total = (data.recent || []).reduce((s, x) => s + (x.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-6">
        <div className="text-sm text-slate-300">Total Commission Earned (recent 100)</div>
        <div className="text-3xl font-bold mt-1">{total.toFixed(2)} <span className="text-lg text-purple-300">AT</span></div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {[1, 2, 3, 4, 5, 6, 7].map((l) => (
          <div key={l} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-center">
            <div className="text-xs text-slate-400">L{l}</div>
            <div className="font-bold text-yellow-300 text-sm">{(data.by_level[l] || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-xl divide-y divide-slate-700 max-h-96 overflow-y-auto">
        {(data.recent || []).map((c) => (
          <div key={c.id} className="p-3 text-sm flex justify-between items-center">
            <div>
              <div className="font-semibold">L{c.level} Commission ({c.rate}%)</div>
              <div className="text-xs text-slate-500">{c.source_type} • Fee total {c.fee_total?.toFixed(2)} AT</div>
              <div className="text-xs text-slate-600 mt-1">{new Date(c.created_at).toLocaleString()}</div>
            </div>
            <div className="text-emerald-400 font-bold">+{c.amount.toFixed(2)} AT</div>
          </div>
        ))}
        {(data.recent || []).length === 0 && <div className="p-6 text-center text-slate-500">No commissions yet. Refer friends!</div>}
      </div>
    </div>
  );
};

// ============== FEE BREAKDOWN TAB ==============
const FeeBreakdownTab = ({ settings }) => {
  const [earnings, setEarnings] = useState(null);
  const [projection, setProjection] = useState(null);
  const [refCount, setRefCount] = useState(10);
  const [avgTrade, setAvgTrade] = useState(5000);

  useEffect(() => {
    api.get('/avx/earnings/summary').then(r => setEarnings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/avx/earnings/projection?new_referrals=${refCount}&avg_monthly_trade_at=${avgTrade}`)
        .then(r => setProjection(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [refCount, avgTrade]);

  const fd = settings?.fee_distribution || {};
  const lvl = settings?.mlm_levels || {};
  const tradeFee = settings?.trading_fee_percent || 3;
  const p2pFee = settings?.p2p_fee_percent || 2;

  // Sample calculations on ₹10,000 trade
  const sampleTrade = 10000;
  const sampleFee = sampleTrade * tradeFee / 100;
  const sampleP2P = 10000;
  const sampleP2PFee = sampleP2P * p2pFee / 100;

  const buckets = [
    { key: 'mlm', label: 'Affiliate Network Commission (7 levels)', color: 'from-purple-500 to-pink-500', icon: Award },
    { key: 'promo', label: 'Promo Wallet', color: 'from-blue-500 to-cyan-500', icon: Send },
    { key: 'govt_tax', label: 'Govt Tax Wallet', color: 'from-red-500 to-orange-500', icon: AlertTriangle },
    { key: 'social_work', label: 'Social Work Wallet', color: 'from-emerald-500 to-teal-500', icon: ShoppingBag },
    { key: 'admin', label: 'Admin Wallet', color: 'from-yellow-500 to-amber-500', icon: Wallet },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <Info className="w-5 h-5 text-yellow-400" />
          <h2 className="text-xl font-bold">100% Fee Distribution</h2>
        </div>
        <p className="text-sm text-slate-300">
          हर trade पर <strong>{tradeFee}%</strong> और हर P2P transfer पर <strong>{p2pFee}%</strong> fee लगती है। यह fee 100% accountable है — कुछ भी "lost" नहीं होता। नीचे देखें कहाँ-कहाँ जाती है:
        </p>
      </div>

      {/* Bucket Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {buckets.map((b) => {
          const Icon = b.icon;
          const pct = fd[b.key] || 0;
          return (
            <div key={b.key} className={`bg-gradient-to-br ${b.color} rounded-xl p-4 text-white shadow-lg`}>
              <Icon className="w-5 h-5 mb-2" />
              <div className="text-xs opacity-80">{b.label}</div>
              <div className="text-3xl font-bold mt-1">{pct}%</div>
            </div>
          );
        })}
      </div>

      {/* Visual Bar */}
      <div>
        <h3 className="text-sm font-semibold text-slate-300 mb-2">Visual Breakdown (out of every ₹100 fee):</h3>
        <div className="flex h-10 rounded-lg overflow-hidden shadow-inner border border-slate-700">
          {buckets.map((b, idx) => {
            const pct = fd[b.key] || 0;
            return (
              <div
                key={b.key}
                className={`bg-gradient-to-r ${b.color} flex items-center justify-center text-xs font-bold text-white`}
                style={{ width: `${pct}%` }}
                title={`${b.label}: ${pct}%`}
              >
                {pct >= 7 ? `${pct}%` : ''}
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
          {buckets.map((b) => (
            <span key={b.key} className="flex items-center gap-1">
              <span className={`w-3 h-3 rounded-sm bg-gradient-to-r ${b.color}`}></span>
              {b.label} ({fd[b.key] || 0}%)
            </span>
          ))}
        </div>
      </div>

      {/* Affiliate Network Levels Detail */}
      <div className="bg-slate-800/40 border border-purple-500/30 rounded-xl p-5">
        <h3 className="text-lg font-semibold text-purple-300 mb-2 flex items-center gap-2">
          <Award className="w-5 h-5" /> Affiliate Network 7-Level Breakdown
        </h3>
        <p className="text-xs text-slate-400 mb-3">
          Affiliate Network का {fd.mlm || 33}% हिस्सा आपके 7 upline sponsors में distribute होता है:
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {[1, 2, 3, 4, 5, 6, 7].map((l) => (
            <div key={l} className="bg-purple-500/20 border border-purple-500/40 rounded-lg p-3 text-center">
              <div className="text-xs text-purple-300">Level {l}</div>
              <div className="text-2xl font-bold text-white">{lvl[l] || lvl[String(l)] || 0}%</div>
              <div className="text-xs text-slate-500 mt-1">of fee</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-400 mt-3">
          Total: <strong className="text-purple-300">{Object.values(lvl).reduce((s, x) => s + (parseFloat(x) || 0), 0)}%</strong> of fee distributed across 7 levels
        </div>
      </div>

      {/* Sample Calculations */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-3 text-emerald-300">📈 Example Trade: ₹10,000 buy</h3>
          <div className="space-y-1 text-sm">
            <Row label="Trade value" value={`₹${sampleTrade.toLocaleString()}`} />
            <Row label={`Fee (${tradeFee}%)`} value={`₹${sampleFee.toFixed(2)}`} highlight />
            <div className="border-t border-slate-700 my-2"></div>
            <Row label={`Affiliate Network (${fd.mlm}%)`} value={`₹${(sampleFee * fd.mlm / 100).toFixed(2)}`} sub />
            <Row label={`Promo (${fd.promo}%)`} value={`₹${(sampleFee * fd.promo / 100).toFixed(2)}`} sub />
            <Row label={`Govt Tax (${fd.govt_tax}%)`} value={`₹${(sampleFee * fd.govt_tax / 100).toFixed(2)}`} sub />
            <Row label={`Social Work (${fd.social_work}%)`} value={`₹${(sampleFee * fd.social_work / 100).toFixed(2)}`} sub />
            <Row label={`Admin (${fd.admin}%)`} value={`₹${(sampleFee * fd.admin / 100).toFixed(2)}`} sub />
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-3 text-blue-300">🔄 Example P2P: ₹10,000 transfer</h3>
          <div className="space-y-1 text-sm">
            <Row label="Transfer amount" value={`₹${sampleP2P.toLocaleString()}`} />
            <Row label={`Fee (${p2pFee}%)`} value={`₹${sampleP2PFee.toFixed(2)}`} highlight />
            <div className="border-t border-slate-700 my-2"></div>
            <Row label={`Affiliate Network (${fd.mlm}%)`} value={`₹${(sampleP2PFee * fd.mlm / 100).toFixed(2)}`} sub />
            <Row label={`Promo (${fd.promo}%)`} value={`₹${(sampleP2PFee * fd.promo / 100).toFixed(2)}`} sub />
            <Row label={`Govt Tax (${fd.govt_tax}%)`} value={`₹${(sampleP2PFee * fd.govt_tax / 100).toFixed(2)}`} sub />
            <Row label={`Social Work (${fd.social_work}%)`} value={`₹${(sampleP2PFee * fd.social_work / 100).toFixed(2)}`} sub />
            <Row label={`Admin (${fd.admin}%)`} value={`₹${(sampleP2PFee * fd.admin / 100).toFixed(2)}`} sub />
          </div>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-xs text-yellow-200">
        💡 <strong>Note:</strong> अगर आपकी upline incomplete है तो जो Affiliate Network commission distribute नहीं हो पाता वो automatically Admin wallet में जाता है — कुछ भी waste नहीं होता।
      </div>

      {/* ============== YOUR EARNINGS ============== */}
      {earnings && (
        <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-5">
          <h3 className="text-lg font-bold text-emerald-300 mb-3 flex items-center gap-2">
            <Award className="w-5 h-5" /> Your Affiliate Network Earnings
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-400">Total Earned</div>
              <div className="text-2xl font-bold text-emerald-300" data-testid="total-earned">
                {(earnings.total_earned || 0).toFixed(2)} <span className="text-sm">AT</span>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-400">Commissions Received</div>
              <div className="text-2xl font-bold text-white">{earnings.total_transactions || 0}</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-400">Direct Referrals</div>
              <div className="text-2xl font-bold text-yellow-300">{earnings.direct_referrals || 0}</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-400">Active Levels</div>
              <div className="text-2xl font-bold text-purple-300">{Object.keys(earnings.by_level || {}).length}/7</div>
            </div>
          </div>

          <div className="mb-4">
            <div className="text-xs text-slate-400 mb-2">Earnings by Level:</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {[1, 2, 3, 4, 5, 6, 7].map((l) => {
                const data = earnings.by_level?.[l] || earnings.by_level?.[String(l)] || { total: 0, count: 0 };
                const has = data.total > 0;
                return (
                  <div key={l} className={`rounded-lg p-2 text-center border ${has ? 'bg-emerald-500/20 border-emerald-500/40' : 'bg-slate-800/40 border-slate-700'}`}>
                    <div className="text-xs text-slate-400">L{l}</div>
                    <div className={`text-sm font-bold ${has ? 'text-emerald-300' : 'text-slate-500'}`}>
                      {data.total.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">{data.count} txns</div>
                  </div>
                );
              })}
            </div>
          </div>

          {(earnings.top_contributors || []).length > 0 && (
            <div>
              <div className="text-xs text-slate-400 mb-2">Top 5 Downline Contributors:</div>
              <div className="space-y-2">
                {earnings.top_contributors.map((c, i) => (
                  <div key={c.username} className="flex items-center justify-between bg-slate-800/40 border border-slate-700 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center font-bold text-black text-sm">
                        #{i + 1}
                      </div>
                      <div>
                        <div className="font-semibold">{c.name || c.username}</div>
                        <div className="text-xs text-slate-500">@{c.username} • {c.transactions} transactions</div>
                      </div>
                    </div>
                    <div className="text-emerald-300 font-bold">+{c.earned.toFixed(2)} AT</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(earnings.total_earned || 0) === 0 && (
            <div className="text-center py-4 text-slate-400 text-sm">
              अभी तक कोई earning नहीं। नीचे Refer & Earn calculator देखें कि कितना कमा सकते हैं!
            </div>
          )}
        </div>
      )}

      {/* ============== REFER & EARN CALCULATOR ============== */}
      <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-5">
        <h3 className="text-lg font-bold text-purple-300 mb-2 flex items-center gap-2">
          🚀 Refer & Earn Calculator
        </h3>
        <p className="text-xs text-slate-400 mb-4">
          देखें कि अगर आप कुछ और लोगों को refer करें तो आपकी monthly कमाई कितनी बढ़ सकती है:
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs text-slate-300 block mb-1">कितने नए लोग refer करेंगे?</label>
            <input
              type="range"
              min="1"
              max="100"
              value={refCount}
              onChange={(e) => setRefCount(parseInt(e.target.value))}
              data-testid="refer-count-slider"
              className="w-full"
            />
            <div className="text-2xl font-bold text-purple-300 text-center">{refCount} लोग</div>
          </div>
          <div>
            <label className="text-xs text-slate-300 block mb-1">हर person कितना AT/month trade करेगा?</label>
            <input
              type="range"
              min="1000"
              max="50000"
              step="500"
              value={avgTrade}
              onChange={(e) => setAvgTrade(parseInt(e.target.value))}
              data-testid="refer-trade-slider"
              className="w-full"
            />
            <div className="text-2xl font-bold text-purple-300 text-center">{avgTrade.toLocaleString()} AT</div>
          </div>
        </div>

        {projection && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl p-4 text-white">
              <div className="text-xs opacity-80">Per Referral / Month</div>
              <div className="text-2xl font-bold">{projection.earning_per_referral_per_month.toFixed(2)} AT</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-600 to-orange-600 rounded-xl p-4 text-white">
              <div className="text-xs opacity-80">Monthly Earning Estimate</div>
              <div className="text-3xl font-bold" data-testid="projection-monthly">{projection.monthly_earning_estimate.toFixed(2)} AT</div>
            </div>
            <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-4 text-white">
              <div className="text-xs opacity-80">Yearly Estimate</div>
              <div className="text-3xl font-bold">{projection.yearly_earning_estimate.toFixed(2)} AT</div>
            </div>
          </div>
        )}

        {projection?.note && (
          <div className="text-xs text-slate-400 mt-3 italic">💡 {projection.note}</div>
        )}

        <div className="mt-4 pt-4 border-t border-slate-700/50 flex flex-wrap gap-2">
          <button
            onClick={() => {
              const link = `${window.location.origin}/register?ref=${(window.localStorage.getItem('username') || '')}`;
              navigator.clipboard.writeText(link);
              toast.success('Referral link copied!');
            }}
            data-testid="copy-referral-btn"
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-sm font-semibold"
          >
            📋 Copy My Referral Link
          </button>
          <a
            href={`https://wa.me/?text=${encodeURIComponent('Join Anant Virtual Exchange (AVX) — Fantasy Trading platform with up to 33% Affiliate Network commission! ' + window.location.origin + '/avx')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-emerald-600 rounded-lg text-sm font-semibold"
          >
            📱 Share on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, highlight, sub }) => (
  <div className={`flex justify-between ${highlight ? 'font-bold text-yellow-300' : sub ? 'text-slate-400 pl-3' : ''}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

// ============== WITHDRAWAL TAB ==============
const WithdrawalTab = ({ settings }) => {
  const [form, setForm] = useState({
    amount: '', method: 'bank',
    account_holder: '', account_number: '', ifsc: '', upi_id: '', note: '',
  });
  const [requests, setRequests] = useState([]);
  const [wallet, setWallet] = useState({});

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [w, r] = await Promise.all([api.get('/avx/wallet/me'), api.get('/avx/withdrawal/me')]);
      setWallet(w.data?.wallet || {});
      setRequests(r.data?.requests || []);
    } catch (e) { /* ignore */ }
  };

  const submit = async () => {
    const amt = parseFloat(form.amount);
    const minW = settings?.min_withdrawal || 5000;
    if (!amt || amt < minW) return toast.error(`Minimum withdrawal is ₹${minW}`);
    if (form.method === 'bank' && (!form.account_holder || !form.account_number || !form.ifsc)) {
      return toast.error('Bank details required (holder name, account, IFSC)');
    }
    if (form.method === 'upi' && !form.upi_id) return toast.error('UPI ID required');
    try {
      await api.post('/avx/withdrawal/request', {
        amount: amt, method: form.method,
        account_holder: form.account_holder, account_number: form.account_number, ifsc: form.ifsc,
        upi_id: form.upi_id, note: form.note,
      });
      toast.success('Withdrawal request submitted! Funds locked.');
      setForm({ amount: '', method: 'bank', account_holder: '', account_number: '', ifsc: '', upi_id: '', note: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-2">Withdraw INR</h2>
        <p className="text-xs text-slate-400 mb-4">
          Available cash: <strong className="text-emerald-300">₹{(wallet.cash_balance || 0).toLocaleString()}</strong> • Min: ₹{settings?.min_withdrawal || 5000}
        </p>

        <input
          type="number"
          placeholder={`Amount (Min ₹${settings?.min_withdrawal || 5000})`}
          value={form.amount}
          onChange={(e) => setForm({...form, amount: e.target.value})}
          data-testid="withdraw-amount"
          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3"
        />

        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setForm({...form, method: 'bank'})}
            className={`flex-1 py-2 rounded-lg font-semibold ${form.method === 'bank' ? 'bg-yellow-500 text-black' : 'bg-slate-700'}`}
          >
            Bank Transfer
          </button>
          <button
            onClick={() => setForm({...form, method: 'upi'})}
            className={`flex-1 py-2 rounded-lg font-semibold ${form.method === 'upi' ? 'bg-yellow-500 text-black' : 'bg-slate-700'}`}
          >
            UPI
          </button>
        </div>

        {form.method === 'bank' ? (
          <>
            <input placeholder="Account holder name" value={form.account_holder} onChange={(e) => setForm({...form, account_holder: e.target.value})} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3" />
            <input placeholder="Account number" value={form.account_number} onChange={(e) => setForm({...form, account_number: e.target.value})} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3" />
            <input placeholder="IFSC code" value={form.ifsc} onChange={(e) => setForm({...form, ifsc: e.target.value.toUpperCase()})} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3" />
          </>
        ) : (
          <input placeholder="UPI ID (e.g. yourname@paytm)" value={form.upi_id} onChange={(e) => setForm({...form, upi_id: e.target.value})} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3" />
        )}

        <textarea placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} rows={2} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3" />

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-3 text-xs text-yellow-200">
          ⚠️ Submit करते ही amount आपके cash wallet से lock हो जाएगा। Admin approve करने पर payout होगा। Reject होने पर refund automatic।
        </div>

        <button onClick={submit} data-testid="withdraw-submit-btn" className="w-full py-3 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg font-bold">
          Submit Withdrawal Request
        </button>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-300 mb-2">My Withdrawals</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {requests.map((r) => (
            <div key={r.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-sm">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold">₹{r.amount.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">{r.method.toUpperCase()} • {r.method === 'bank' ? `A/c ${r.account_number}` : r.upi_id}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${r.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : r.status === 'rejected' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                  {r.status}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{new Date(r.created_at).toLocaleString()}</div>
              {r.payout_reference && <div className="text-xs text-emerald-400 mt-1">Ref: {r.payout_reference}</div>}
              {r.admin_note && <div className="text-xs text-slate-400 mt-1 italic">"{r.admin_note}"</div>}
            </div>
          ))}
          {requests.length === 0 && <div className="text-slate-500 text-sm">No withdrawals yet</div>}
        </div>
      </div>
    </div>
  );
};

// ============== LEADERBOARD TAB ==============
const LeaderboardTab = () => {
  const [metric, setMetric] = useState('volume');
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/avx/leaderboard?metric=${metric}&limit=10`)
      .then(r => setLeaders(r.data?.leaders || []))
      .finally(() => setLoading(false));
  }, [metric]);

  const metricLabel = { volume: 'Trading Volume', trades: 'Trade Count', portfolio: 'Portfolio Value' }[metric];
  const valueOf = (l) => metric === 'volume' ? `${l.volume.toLocaleString()} AT`
    : metric === 'trades' ? `${l.trades} trades`
    : `${l.portfolio_value.toLocaleString()} AT`;

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-5">
        <h2 className="text-2xl font-bold flex items-center gap-2 text-yellow-300">
          <Trophy className="w-6 h-6" /> Top Traders Leaderboard
        </h2>
        <p className="text-xs text-slate-400 mt-1">अनonymized usernames, top 10 by {metricLabel}</p>
      </div>

      <div className="flex gap-2">
        {[
          { id: 'volume', label: '💰 Volume' },
          { id: 'trades', label: '📊 Trades' },
          { id: 'portfolio', label: '💎 Portfolio' },
        ].map(m => (
          <button
            key={m.id}
            onClick={() => setMetric(m.id)}
            data-testid={`leaderboard-metric-${m.id}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${metric === m.id ? 'bg-yellow-500 text-black' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading leaderboard...</div>
      ) : leaders.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-slate-800/40 rounded-xl border border-slate-700">
          अभी तक कोई trader नहीं। पहले बनिए!
        </div>
      ) : (
        <div className="space-y-2">
          {leaders.map((l) => {
            const rankColors = {
              1: 'from-yellow-500 to-orange-500',
              2: 'from-slate-400 to-slate-500',
              3: 'from-orange-700 to-amber-800',
            };
            const medalColors = rankColors[l.rank] || 'from-slate-700 to-slate-800';
            return (
              <div
                key={l.user_id}
                className={`flex items-center gap-3 p-4 rounded-xl border transition ${
                  l.is_self
                    ? 'bg-yellow-500/15 border-yellow-500/50 ring-2 ring-yellow-500/40'
                    : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${medalColors} flex items-center justify-center font-bold text-white text-lg`}>
                  {l.rank <= 3 ? (l.rank === 1 ? '🥇' : l.rank === 2 ? '🥈' : '🥉') : `#${l.rank}`}
                </div>
                <div className="flex-1">
                  <div className={`font-bold ${l.is_self ? 'text-yellow-300' : ''}`}>{l.display_name}</div>
                  <div className="text-xs text-slate-500">
                    Volume: {l.volume.toLocaleString()} AT • Trades: {l.trades}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-yellow-300">{valueOf(l)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AVX;
